import { useState } from "react";
import { InputField } from "../../common/ui/inputField";
import { TimeSelect } from "../../common/ui/timeSelect";

interface ReportSectionCardProps {
  title: string;
  description?: string;
  enabled: boolean;
  onToggle: () => void;
  time?: string;
  onTimeChange?: (v: string) => void;
  timeId?: string;
  timeLabel?: string;
  subject: string;
  onSubjectChange: (v: string) => void;
  subjectId: string;
  subjectLabel: string;
  subjectPlaceholder: string;
  children?: React.ReactNode;
  recipientsSlot?: React.ReactNode;
}

export const ReportSectionCard = ({
  title,
  description,
  enabled,
  onToggle,
  time,
  onTimeChange,
  timeId,
  timeLabel,
  subject,
  onSubjectChange,
  subjectId,
  subjectLabel,
  subjectPlaceholder,
  children,
  recipientsSlot,
}: ReportSectionCardProps) => {
  const hasTime = time !== undefined && onTimeChange !== undefined;
  const [collapsed, setCollapsed] = useState(true);

  const collapseLabel = "Colapsar sección";
  const expandLabel = "Expandir sección";

  return (
    <div className="p-4 bg-global-overlay-secondary rounded-lg border border-global-secondary space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`shrink-0 overflow-hidden transition-[max-width] duration-200 ease-out ${enabled ? "max-w-8" : "max-w-0"}`}
            aria-hidden={!enabled}
          >
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? expandLabel : collapseLabel}
              title={collapsed ? expandLabel : collapseLabel}
              className={`shrink-0 p-1 rounded appearance-none bg-transparent! text-global-secondary hover:bg-transparent! hover:text-global-primary active:!bg-transparent! focus:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-global-ring transition-opacity duration-200 ${enabled ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              style={{ backgroundColor: "transparent" }}
            >
              {collapsed ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              )}
            </button>
          </div>
          <h3 className="text-size-sm font-semibold text-global-primary truncate">
            {title}
          </h3>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={onToggle}
          className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus-global-ring shrink-0"
          style={{
            backgroundColor: enabled
              ? "var(--btn-primary)"
              : "var(--color-toggle-inactive)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = enabled
              ? "var(--btn-primary-hover)"
              : "var(--color-toggle-inactive-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = enabled
              ? "var(--btn-primary)"
              : "var(--color-toggle-inactive)";
          }}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {enabled && !collapsed && (
        <>
          {description && (
            <p className="text-size-2xs text-global-secondary -mt-2">
              {description}
            </p>
          )}

          <div
            className={
              hasTime
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                : "grid grid-cols-1 gap-4"
            }
          >
            {hasTime && (
              <TimeSelect
                id={timeId!}
                label={timeLabel!}
                value={time!}
                onChange={onTimeChange!}
              />
            )}
            <InputField
              id={subjectId}
              type="text"
              label={subjectLabel}
              placeholder={subjectPlaceholder}
              value={subject}
              onChange={(e) => onSubjectChange(e.target.value)}
            />
          </div>

          {children}

          {recipientsSlot && <div className="space-y-2">{recipientsSlot}</div>}
        </>
      )}
    </div>
  );
};
